import React from 'react'
import styled from '@emotion/styled'
import { UserPreference } from 'api'
import { BannerTileContainer, CurrencyTotalTilesContainer } from './heroPanelStyles'
import { CashAndNotionalAmount, getDirectionText } from './utils'
import { Decimal } from 'utils'
import { formatNumber } from 'locale/Locale'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'
import { BannerTilesGroup } from './BannerTilesGroup'

interface BannerCurrencyTotalTileProps {
  cashAndNotionalAmount: CashAndNotionalAmount
  userPreference: UserPreference
}

const NetAmount = styled.div<{ negative: boolean }>`
  font-family: NEXWeb;
  font-size: 16px;
  font-weight: ${constants.FontWeight.Medium};
  color: ${props => (props.negative ? constants.DIRECTION_SELL : constants.DIRECTION_BUY)};
  text-transform: uppercase;
  white-space: nowrap;
  margin-bottom: 4px;
  margin-left: auto;
`

const CurrencyTotalsRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 3px;
  &:last-of-type {
    margin-bottom: 0;
  }
`

const CurrencyTotalsLabel = styled.span<{ labelWidth: number }>`
  width: ${props => props.labelWidth}px;
  font-family: ProximaNova;
  font-size: 12px;
  line-height: 15px;
  color: ${constants.CORE_PRIMARY};
  opacity: 0.75;
  text-transform: uppercase;
`

const DirectionCount = styled.span`
  font-size: 12px;
  font-family: ProximaNova;
  font-weight: ${constants.FontWeight.SemiBold};
  color: ${constants.CORE_PRIMARY};
  opacity: 0.75;
  margin-right: 17px;
`

const CurrencyTotalAmount = styled.span`
  margin-left: auto;
  font-family: NEXWeb;
  font-size: 14px;
  font-weight: ${constants.FontWeight.Medium};
  color: ${constants.CORE_PRIMARY};
`

const TotalsTileBody = styled.div`
  max-width: 230px;
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-right: 10px;
`

export const BannerCurrencyTotalTile: React.FunctionComponent<BannerCurrencyTotalTileProps> = props => {
  const { cashAndNotionalAmount, userPreference } = props
  const netStartCash = Decimal.subtract(cashAndNotionalAmount.cashSellAmount, cashAndNotionalAmount.cashBuyAmount)
  const netNotional = Decimal.subtract(cashAndNotionalAmount.notionalBuyAmount, cashAndNotionalAmount.notionalSellAmount)
  const [buyDirection, sellDirection, directionLabelWidth] = getDirectionText(userPreference.directionFormat)
  const absoluteCash = Decimal.add(cashAndNotionalAmount.cashBuyAmount, cashAndNotionalAmount.cashSellAmount)
  const absoluteNotional = Decimal.add(cashAndNotionalAmount.notionalBuyAmount, cashAndNotionalAmount.notionalSellAmount)

  return (
    <CurrencyTotalTilesContainer>
      <BannerTilesGroup title={cashAndNotionalAmount.currency} columnName={'NOTIONAL'}>
        <BannerTileContainer removeBorder={true}>
          <TotalsTileBody>
            <CurrencyTotalsRow>
              <CurrencyTotalsLabel labelWidth={80}>{Locale.translate(Translation.TC_HERO_NET)}</CurrencyTotalsLabel>
              <NetAmount negative={netNotional < 0}>{formatNumber(netNotional, { minDp: 2, parenOnNegative: false })}</NetAmount>
            </CurrencyTotalsRow>
            <CurrencyTotalsRow>
              <CurrencyTotalsLabel labelWidth={directionLabelWidth}>{buyDirection}</CurrencyTotalsLabel>
              <DirectionCount>
                {'#'}
                {cashAndNotionalAmount.notionalBuyCount}
              </DirectionCount>
              <CurrencyTotalAmount>{formatNumber(cashAndNotionalAmount.notionalBuyAmount, { minDp: 2 })}</CurrencyTotalAmount>
            </CurrencyTotalsRow>
            <CurrencyTotalsRow>
              <CurrencyTotalsLabel labelWidth={directionLabelWidth}>{sellDirection}</CurrencyTotalsLabel>{' '}
              <DirectionCount>
                {'#'}
                {cashAndNotionalAmount.notionalSellCount}
              </DirectionCount>
              <CurrencyTotalAmount>{formatNumber(cashAndNotionalAmount.notionalSellAmount, { minDp: 2 })}</CurrencyTotalAmount>
            </CurrencyTotalsRow>
            <CurrencyTotalsRow>
              <CurrencyTotalsLabel labelWidth={80}>{Locale.translate(Translation.TC_HERO_ABSOLUTE)}</CurrencyTotalsLabel>
              <CurrencyTotalAmount>{formatNumber(absoluteNotional, { minDp: 2 })}</CurrencyTotalAmount>
            </CurrencyTotalsRow>
          </TotalsTileBody>
        </BannerTileContainer>
      </BannerTilesGroup>
      <BannerTilesGroup columnName={'CASH'}>
        <BannerTileContainer>
          <TotalsTileBody>
            <CurrencyTotalsRow>
              <NetAmount negative={netStartCash < 0}>{formatNumber(netStartCash, { minDp: 2, parenOnNegative: false })}</NetAmount>
            </CurrencyTotalsRow>
            <CurrencyTotalsRow>
              <CurrencyTotalAmount>{formatNumber(cashAndNotionalAmount.cashBuyAmount, { minDp: 2 })}</CurrencyTotalAmount>
            </CurrencyTotalsRow>
            <CurrencyTotalsRow>
              <CurrencyTotalAmount>{formatNumber(cashAndNotionalAmount.cashSellAmount, { minDp: 2 })}</CurrencyTotalAmount>
            </CurrencyTotalsRow>
            <CurrencyTotalsRow>
              <CurrencyTotalAmount>{formatNumber(absoluteCash, { minDp: 2 })}</CurrencyTotalAmount>
            </CurrencyTotalsRow>
          </TotalsTileBody>
        </BannerTileContainer>
      </BannerTilesGroup>
    </CurrencyTotalTilesContainer>
  )
}
